import Vue from 'vue';
import App from './App.vue';
import store from './store';

import * as signalR from '@aspnet/signalr';
import SvgIcon from 'vue-svgicon';
import VueApollo from 'vue-apollo';
import VueTouch from 'vue-touch';
import axios from 'axios';
import { getRouter } from './router/router';
import UtilsI18n from './plugins/i18n';
import interceptorsSetup from './helpers/httpInterceptor.js';
import graphqlClient from './helpers/graphqlClient';
import filters from './scripts/filters';
/*import { ApplicationInsights } from '@microsoft/applicationinsights-web';*/
import VueNumeric from 'vue-numeric';
import VueCurrencyInput from 'vue-currency-input';
import consoleMessagesSetup from './helpers/consoleMessages.js';
import PerfectScrollbar from 'vue2-perfect-scrollbar';
import 'vue2-perfect-scrollbar/dist/vue2-perfect-scrollbar.css';
import VueAnnouncer from '@vue-a11y/announcer';
import MagicGrid from 'vue-magic-grid';
import VueGtag from 'vue-gtag';
import { setStorageData } from './helpers/storageFunctions';
import AppNoConnection from '@/AppNoConnection.vue';

/*const appInsights = new ApplicationInsights({
	config: {
		instrumentationKey: process.env.VUE_APP_AZURE_INSIGHTS_KEY,
		loggingLevelConsole: 2,
		loggingLevelTelemetry: 2
	}
});
appInsights.loadAppInsights();*/

// Intercept browser's back button
window.popStateDetected = false;
window.addEventListener('popstate', () => {
	window.popStateDetected = true;
});

filters.create(Vue);

Vue.config.productionTip = false;

Vue.use(VueCurrencyInput);
Vue.use(VueNumeric);
Vue.use(VueTouch, { name: 'v-touch' });
Vue.use(SvgIcon, {
	tagName: 'svgicon'
});
Vue.use(VueApollo);
Vue.use(PerfectScrollbar);
Vue.use(MagicGrid);

const apolloProvider = new VueApollo({
	defaultClient: graphqlClient
});

axios.defaults.baseURL = process.env.VUE_APP_API_URL;

consoleMessagesSetup();

let reconnectionTries = 0;

const MAX_RECONNECTION_TRIES = 2;
// Value in ms
const RECONNECTION_TIME = 5000;
const TIMEOUT_RECONNECTS = [];

initSignalR().then(() => {
	UtilsI18n.initI18n()
		.then(i18n => {
			const router = getRouter();
			interceptorsSetup(router);

			// Enable Google Analytics for all available routes
			Vue.use(
				VueGtag,
				{
					config: { id: process.env.VUE_APP_GOOGLE_ANALYTICS }
				},
				router
			);

			Vue.use(
				VueAnnouncer,
				{
					complementRoute: i18n.tc('loaded')
				},
				router
			);
			new Vue({
				i18n,
				store,
				router,
				apolloProvider,
				render: h => h(App)
			}).$mount('#app');
		})
		.catch(function(e) {
			console.error(e);
			new Vue({
				render: h => h(AppNoConnection)
			}).$mount('#app');
		});
});

let handleOutsideClick;
Vue.directive('click-outside', {
	bind(el, binding, vnode) {
		handleOutsideClick = e => {
			e.stopPropagation();
			const { handler, exclude } = binding.value;
			let clickedOnExcludedEl = false;
			let clickedOnExcludedElArray = [];

			exclude.forEach(refName => {
				if (!clickedOnExcludedEl) {
					let excludedEl = vnode.context.$refs[refName];
					if (Array.isArray(excludedEl)) {
						excludedEl.forEach(component => {
							if (component) {
								if (component.$el) {
									excludedEl = component.$el;
								}
								clickedOnExcludedElArray.push(excludedEl.contains(e.target));
							}
						});
						if (clickedOnExcludedElArray.includes(true)) {
							return;
						} else if (!el.contains(e.target) && !clickedOnExcludedEl) {
							vnode.context[handler]();
						}
					} else {
						if (excludedEl) {
							if (excludedEl.$el) {
								excludedEl = excludedEl.$el;
							}
							clickedOnExcludedEl = excludedEl.contains(e.target);
						}
						if (!el.contains(e.target) && !clickedOnExcludedEl) {
							vnode.context[handler]();
						}
					}
				}
			});
		};

		document.addEventListener('click', handleOutsideClick);
		document.addEventListener('touchstart', handleOutsideClick);
	},
	unbind() {
		document.removeEventListener('click', handleOutsideClick);
		document.removeEventListener('touchstart', handleOutsideClick);
	}
});

async function initSignalR() {
	var connection = new signalR.HubConnectionBuilder()
		.withUrl(`${process.env.VUE_APP_API_URL}/jaHub`)
		.build();

	Vue.prototype.signalRConnection = connection;

	connection.on('ReceiveMessage', (user, message) => {
		// eslint-disable-next-line no-console
		console.log(message);
	});

	connection.on('StudentLogin', () => {
		store.dispatch('sessionLost');
	});

	connection.on('SessionExpired', () => {
		store.dispatch('logout');
	});

	connection.on('RefreshSession', () => {
		store.dispatch('isUserAuthenticated').then(response => {
			if (response) {
				store.dispatch('keepSessionAlive');
			}
		});
	});

	connection.on('SetSignalrId', srid => {
		sessionStorage.setItem('srid', srid);
	});

	connection.on('SetCookieExpiresUtc', cookieDate => {
		setStorageData('ceUTC', cookieDate);

		// If we have updated cookie expiration date
		// close All modals (in all opened windows)
		// related to "Session is about the expire"
		store.commit('setSessionWillExpireState', false);
	});

	connection.onclose(async () => {
		// eslint-disable-next-line no-console
		console.log('Connection closed');
		if (reconnectionTries < MAX_RECONNECTION_TRIES) {
			await startConnection();
		}
	});

	async function startConnection() {
		try {
			await connection.start();
			// eslint-disable-next-line no-console
			console.log('Connection started');

			// Kill all timers if connection is established
			TIMEOUT_RECONNECTS.forEach(t => clearTimeout(t));
			// Reset number of tries
			reconnectionTries = 0;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.log(error);
			if (
				connection.state === signalR.HubConnectionState.Disconnected &&
				reconnectionTries < MAX_RECONNECTION_TRIES
			) {
				// increment tries
				reconnectionTries++;
				// call this function after time. The time is progressively incremented based on number of tries
				// ex: first Try: 5000 * 1; second Try: 5000*2
				const t = setTimeout(
					() => startConnection(),
					RECONNECTION_TIME * reconnectionTries
				);
				TIMEOUT_RECONNECTS.push(t);
			}
		}
	}

	startConnection();
}
